import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';

import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import { FeatureFlags } from '~constants/featureFlags';
import { EntityCSVConfig } from '~hooks/useCSVImportExport/configs/types';
import useIsCompanySuspended from '~hooks/useIsCompanySuspended/useIsCompanySuspended';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface CSVEntityImportActionProps<T, TExport = T> {
  featureFlagKey: FeatureFlags;
  canCreateImportedEntity: boolean;
  entityDisplayName: string;
  config: EntityCSVConfig<T, TExport>;
  onImportComplete: () => void;
}

export function CSVEntityImportAction<T, TExport = T>({
  featureFlagKey,
  canCreateImportedEntity,
  entityDisplayName,
  config,
  onImportComplete,
}: CSVEntityImportActionProps<T, TExport>) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const isImportEnabled = useFeatureFlag({ featureFlagKey });

  // Bulk-creating records is a write, so it stays blocked while billing is suspended,
  // matching the other create buttons beside it.
  const isCompanySuspended = useIsCompanySuspended();

  const importModal = useCSVModal({ onImportComplete });

  if (!isImportEnabled || !canCreateImportedEntity) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={isCompanySuspended ? t('billing.contact_billing_to_enable_access') : ''}
      >
        <span data-test-id="csv-import-tooltip-target">
          <CSVImportExportButton
            disabled={isCompanySuspended}
            onClick={importModal.open}
          />
        </span>
      </Tooltip>

      <CSVImportExportModal
        companyId={companyId}
        config={config}
        entityDisplayName={entityDisplayName}
        isOpen={importModal.isOpen}
        onClose={importModal.close}
        onImportComplete={importModal.handleImportComplete}
      />
    </>
  );
}
